import { useEffect, useRef, useState } from 'react';
import { HttpTransportType, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getDocumentStatusConfig } from '../api/document-upload';
import { getAccessToken } from '../api/localstorage';
import {
    CONNECTION_STATES,
    DOCUMENT_STATUS_CHANGED,
    POLL_INTERVAL_MS,
    RECONNECT_DELAYS_MS,
    hubAccessToken,
    shouldPoll,
} from '../../components/Pages/Enquiries/RaiseEnquiry/documentStatusUpdates';

const ACCOUNT_STORAGE_KEY = 'account';

export function useDocumentStatusConnection({ active, onStatus, onResync }) {
    const [connectionState, setConnectionState] = useState(CONNECTION_STATES.IDLE);
    const [loginVersion, setLoginVersion] = useState(0);
    const onStatusRef = useRef(onStatus);
    const onResyncRef = useRef(onResync);
    onStatusRef.current = onStatus;
    onResyncRef.current = onResync;

    useEffect(() => {
        if (!active) {
            setConnectionState(CONNECTION_STATES.IDLE);
            return undefined;
        }

        let stopped = false;
        let connection = null;
        let tokenInUse = getAccessToken();
        const setState = (state) => {
            if (!stopped) setConnectionState(state);
        };
        const resync = () => {
            if (!stopped) onResyncRef.current();
        };

        setState(CONNECTION_STATES.CONNECTING);

        getDocumentStatusConfig()
            .then((resp) => {
                const hubUrl = resp?.data?.hubUrl;
                if (stopped) return undefined;
                if (!hubUrl) {
                    setState(CONNECTION_STATES.DISCONNECTED);
                    return undefined;
                }

                connection = new HubConnectionBuilder()
                    .withUrl(hubUrl, {
                        skipNegotiation: true,
                        transport: HttpTransportType.WebSockets,
                        accessTokenFactory: () => {
                            tokenInUse = getAccessToken();
                            return hubAccessToken(tokenInUse);
                        },
                    })
                    .withAutomaticReconnect(RECONNECT_DELAYS_MS)
                    .configureLogging(LogLevel.Warning)
                    .build();

                connection.on(DOCUMENT_STATUS_CHANGED, (update) => {
                    if (!stopped) onStatusRef.current(update);
                });
                connection.onreconnecting(() => setState(CONNECTION_STATES.RECONNECTING));
                connection.onreconnected(() => {
                    setState(CONNECTION_STATES.CONNECTED);
                    resync();
                });
                connection.onclose(() => setState(CONNECTION_STATES.DISCONNECTED));

                return connection.start().then(() => {
                    setState(CONNECTION_STATES.CONNECTED);
                    resync();
                });
            })
            .catch(() => setState(CONNECTION_STATES.DISCONNECTED));

        const onStorage = (event) => {
            if (event.key === ACCOUNT_STORAGE_KEY && getAccessToken() && getAccessToken() !== tokenInUse) {
                setLoginVersion((version) => version + 1);
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            stopped = true;
            window.removeEventListener('storage', onStorage);
            if (connection) connection.stop().catch(() => {});
        };
    }, [active, loginVersion]);

    useEffect(() => {
        if (!shouldPoll(active, connectionState)) return undefined;
        const timer = setInterval(() => onResyncRef.current(), POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [active, connectionState]);

    return connectionState;
}
